// Capture a live DOM element to a PNG using the Element Capture API.

declare global {
  interface DisplayMediaStreamOptions {
    preferCurrentTab?: boolean;
    selfBrowserSurface?: "include" | "exclude";
    surfaceSwitching?: "include" | "exclude";
  }
  class CropTarget {
    static fromElement(el: Element): Promise<CropTarget>;
  }
  class RestrictionTarget {
    static fromElement(el: Element): Promise<RestrictionTarget>;
  }
  interface MediaStreamTrack {
    cropTo?(target: CropTarget | null): Promise<void>;
    restrictTo?(target: RestrictionTarget | null): Promise<void>;
  }
  class ImageCapture {
    constructor(track: MediaStreamTrack);
    grabFrame(): Promise<ImageBitmap>;
  }
}

let cachedTrack: MediaStreamTrack | null = null;

async function acquireTrack(): Promise<MediaStreamTrack> {
  if (cachedTrack && cachedTrack.readyState === "live") return cachedTrack;
  const stream = await navigator.mediaDevices.getDisplayMedia({
    preferCurrentTab: true,
    selfBrowserSurface: "include",
    video: { frameRate: 30 },
    audio: false,
  } as DisplayMediaStreamOptions);
  const [track] = stream.getVideoTracks();
  cachedTrack = track;
  track.addEventListener("ended", () => {
    cachedTrack = null;
  });
  return track;
}

function fmtErr(e: unknown, label: string): Error {
  if (e instanceof Error) return new Error(`${label}: ${e.name} ${e.message}`);
  if (e === undefined) return new Error(`${label}: rejected with undefined`);
  try {
    return new Error(`${label}: ${JSON.stringify(e)}`);
  } catch {
    return new Error(`${label}: ${String(e)}`);
  }
}

async function step<T>(label: string, p: Promise<T> | (() => Promise<T>)): Promise<T> {
  const started = performance.now();
  try {
    const value = typeof p === "function" ? await p() : await p;
    console.log(`[capture] ${label}: ok (${(performance.now() - started).toFixed(0)}ms)`);
    return value;
  } catch (e) {
    console.log(`[capture] ${label}: FAIL`, e);
    throw fmtErr(e, label);
  }
}

export async function captureElementToBlob(el: HTMLElement): Promise<Blob> {
  const hasCrop = "CropTarget" in window;
  const hasRestrict = "RestrictionTarget" in window;
  if (!hasCrop && !hasRestrict) {
    throw new Error("CropTarget/RestrictionTarget not supported — need Chrome 104+/116+.");
  }

  const track = await step("acquireTrack", acquireTrack);

  if (hasCrop && typeof track.cropTo === "function") {
    const target = await step("CropTarget.fromElement", CropTarget.fromElement(el));
    await step("track.cropTo", track.cropTo(target));
  } else if (hasRestrict && typeof track.restrictTo === "function") {
    const target = await step("RestrictionTarget.fromElement", RestrictionTarget.fromElement(el));
    await step("track.restrictTo", track.restrictTo(target));
  } else {
    throw new Error("track lacks cropTo/restrictTo despite API presence.");
  }

  // give the compositor a frame to apply the crop
  await new Promise((r) => requestAnimationFrame(() => r(null)));

  const capture = new ImageCapture(track);
  const bitmap = await step("grabFrame", capture.grabFrame());

  const targetW = el.offsetWidth;
  const targetH = el.offsetHeight;
  console.log(`[capture] bitmap ${bitmap.width}x${bitmap.height} -> png ${targetW}x${targetH}`);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
  });
}

export function stopCapture() {
  if (cachedTrack) {
    cachedTrack.stop();
    cachedTrack = null;
  }
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
